import styled from '@emotion/styled'
import { getHumanDateFromTimestamp } from '@pubstack/common/src/date'
import { adUnitsDownloadFileId, allMappingFileId, biddersMappingDownloadFileId, MappingDownloadType, MappingExport } from '@pubstack/common/src/mappingExport'
import { AdminSite } from '@pubstack/common/src/site'
import { FunctionComponent, useCallback, useState } from 'react'
import { useUpdateAdminSite } from '~/api/admin/admin-site.api'
import { useCreateMappingExport } from '~/api/admin/mapping-export.api'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import Button from '~/components/Button'
import { FileDownload } from '~/components/FileDownload'
import { Flyout } from '~/components/Flyout'
import { Spinner } from '~/components/Spinner'
import { Toggle } from '~/components/Toggle'
import { Tooltip } from '~/components/Tooltip'

const MappingRetrievalSectionTitle = styled.h2`
  ${Fonts.H2}
  font-weight: 500;
  margin-bottom: 1rem;
`

const ToggleLabel = styled.span`
  margin-left: 0.5rem;
`

const StartedTime = styled.div`
  margin-left: 52px;
  ${Fonts.P2};
  color: ${Colors.SlateGrey};
`

const FileGenerationSectionTitle = styled.h2`
  ${Fonts.H2}
  font-weight: 500;
  margin-top: 1.5rem;
  margin-bottom: 1rem;
`

const GenerateFilesSection = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
`

const MappingExportSection = styled.div`
  text-align: center;
`

const FileDownloadSection = styled.div`  
  margin-right: 0.25rem;
  
  > * {
    width: 100%;
    padding: 16px 0;
    border-top: 1px solid ${Colors.Platinum};
  }
  :last-of-type > * {
    border-bottom: 1px solid ${Colors.Platinum};
  }
`

const DownloadAllSection = styled.div`
  margin-top: 1rem;
  display: flex;
  justify-content: end;
`

type Props = {
  isInitialLoading: boolean
  isDownloading: Record<MappingDownloadType, boolean>
  site?: AdminSite
  mappingExportLoading: boolean
  mappingExport?: MappingExport
  onRefreshClick: () => void
  onDownloadClick: (type: MappingDownloadType, fileName: string) => void
}

const PureAdminScopeSiteSetupFlyout: FunctionComponent<Props> = ({ isInitialLoading, isDownloading, site, mappingExportLoading, mappingExport, onRefreshClick, onDownloadClick }) => {
  // Context
  const scopeId = site?.scopeId ?? ''
  const siteId = site?.id ?? ''

  // State
  const [localSite, setLocalSite] = useState<AdminSite | undefined>(site)
  // Resync the editable copy when the site prop changes ("previous value" pattern).
  const [prevSite, setPrevSite] = useState(site)
  if (site !== prevSite) {
    setPrevSite(site)
    setLocalSite(site)
  }

  // Mutations
  const { mutateAsync: editSite } = useUpdateAdminSite(scopeId, siteId, true, {
    successToast: (
      <>
        <strong>{localSite?.name}</strong> was successfully updated,
      </>
    ),
    errorToast: 'Something went wrong when trying to update this site',
  })
  const { mutateAsync: createMappingExport, isPending: isCreatingMappingExport } = useCreateMappingExport(scopeId, siteId)

  const filenamePlaceholder = mappingExport?.status === 'INPROGRESS' ? 'creating file...' : mappingExport?.status === 'FAILED' ? 'error while creating file, retry generation' : 'no file'

  const toggleMappingRetrievalChange = useCallback(async () => {
    if (!localSite) return

    const admOnboarding = !localSite.admOnboarding
    const updatedSite: AdminSite = { ...localSite, admOnboarding }

    setLocalSite(updatedSite)

    await editSite(updatedSite)
  }, [localSite, editSite])

  const handleFilesGenerationClick = useCallback(async () => {
    if (!scopeId || !siteId) return

    await createMappingExport({ scopeId, tagId: siteId })
  }, [scopeId, siteId, createMappingExport])

  return (
    <Flyout.Content>
      {!localSite || isInitialLoading ? (
        <Spinner />
      ) : (
        <>
          <Flyout.Title>{localSite.name}</Flyout.Title>

          <Flyout.Body>
            <MappingRetrievalSectionTitle>Gather inventory information such as ad units and mapping</MappingRetrievalSectionTitle>

            <Tooltip title="Activate mapping retrieval from the collector">
              <Toggle value={localSite.admOnboarding} onClick={toggleMappingRetrievalChange} trackingLabel={`admin-adm-onboarding-${localSite.admOnboarding ? 'unset' : 'set'}`} />
              <ToggleLabel>Mapping retrieval</ToggleLabel>
            </Tooltip>
            <StartedTime>{localSite.admOnboardingEnabledTime ? `Started on: ${getHumanDateFromTimestamp(localSite.admOnboardingEnabledTime, 'dd LLL yyyy, hh:mm a')}` : 'Not started'}</StartedTime>

            <FileGenerationSectionTitle>Generate configuration files</FileGenerationSectionTitle>
            <GenerateFilesSection>
              <Button disabled={mappingExportLoading || isCreatingMappingExport} variant="secondary" onClick={handleFilesGenerationClick} trackingLabel="admin-adm-onboarding-files-generation-new">
                Generate files
              </Button>
              <Button
                disabled={mappingExportLoading || isCreatingMappingExport || !mappingExport?.id}
                variant={'tertiary'}
                iconName={'refresh'}
                onClick={onRefreshClick}
                trackingLabel={'admin-adm-onboarding-files-generation-refresh'}
              >
                Refresh
              </Button>
            </GenerateFilesSection>

            <MappingExportSection>
              {mappingExportLoading || isCreatingMappingExport ? (
                <Spinner />
              ) : (
                <>
                  <FileDownloadSection>
                    <FileDownload
                      label="Ad units list"
                      filenamePlaceholder={filenamePlaceholder}
                      filename={mappingExport?.status === 'CREATED' ? `${adUnitsDownloadFileId(localSite.name)}.csv` : undefined}
                      trackingLabel="admin-adm-onboarding-ad-units-download"
                      isDownloading={isDownloading['adunits']}
                      onDownload={() => mappingExport?.id && onDownloadClick('adunits', adUnitsDownloadFileId(localSite.name))}
                    />
                  </FileDownloadSection>
                  <FileDownloadSection>
                    <FileDownload
                      label="Mapping"
                      filenamePlaceholder={filenamePlaceholder}
                      filename={mappingExport?.status === 'CREATED' ? `${biddersMappingDownloadFileId(localSite.name)}.zip` : undefined}
                      trackingLabel="admin-adm-onboarding-mapping-download"
                      isDownloading={isDownloading['bidders-mapping']}
                      onDownload={() => mappingExport?.id && onDownloadClick('bidders-mapping', biddersMappingDownloadFileId(localSite.name))}
                    />
                  </FileDownloadSection>

                  {mappingExport?.status === 'CREATED' && (
                    <DownloadAllSection>
                      <Button
                        variant="primary"
                        disabled={isDownloading['all']}
                        iconSpin={isDownloading['all']}
                        iconName={isDownloading['all'] ? 'pubstack' : 'download'}
                        trackingLabel="admin-adm-onboarding-mapping-all-download"
                        onClick={() => mappingExport.id && onDownloadClick('all', allMappingFileId(localSite.name))}
                      >
                        Download files
                      </Button>
                    </DownloadAllSection>
                  )}
                </>
              )}
            </MappingExportSection>
          </Flyout.Body>
        </>
      )}
    </Flyout.Content>
  )
}

export default PureAdminScopeSiteSetupFlyout
